import { Button } from '@buakei/ui';
import { ArrowRight, BookOpenCheck, ShieldCheck, UserCheck } from 'lucide-react';
import { useEffect } from 'react';
import { Link } from 'react-router';
import { Brand } from '@/shell/Brand';
import { ExperienceShowcase } from './ExperienceShowcase';

function useTitle(title: string) {
  useEffect(() => {
    document.title = title;
  }, [title]);
}

const PRINCIPLES = [
  {
    icon: BookOpenCheck,
    title: 'Honest about coverage',
    body: 'Buakei AI says what it searched. “Not found” never means “no such law exists”, and known gaps in the collection are shown alongside every answer.',
  },
  {
    icon: UserCheck,
    title: 'Lawyers stay in charge',
    body: 'AI drafts are assistive. Consequential work needs review by a qualified lawyer, and nothing is sent, filed or settled on your behalf.',
  },
  {
    icon: ShieldCheck,
    title: 'Confidentiality first',
    body: 'Firm, matter, personal and student data are kept apart. No client files are accepted until the required security checks have passed.',
  },
];

export function HomePage() {
  useTitle('Buakei AI — Sierra Leone legal research and learning');
  return (
    <>
      <section className="on-navy overflow-hidden bg-navy-950 text-white">
        <div className="mx-auto grid max-w-6xl items-center gap-12 px-4 py-16 md:grid-cols-[1fr_auto] md:py-24">
          <div className="flex max-w-4xl flex-col">
            <h1 className="font-display text-[2.75rem] leading-[1.05] text-balance md:text-7xl">
              Source-grounded legal research and learning,{' '}
              <span className="text-navy-200">built for Sierra Leone.</span>
            </h1>
            <p className="mt-8 max-w-2xl text-lg leading-relaxed text-white/90">
              One platform for legal professionals and law students, with an assistant that shows
              its sources and is honest about what it cannot establish.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4">
              <Button asChild variant="inverse" size="lg">
                <Link to="/sign-in">Explore the demo</Link>
              </Button>
              <Link
                to="/security"
                className="inline-flex items-center gap-2 font-medium text-white underline-offset-4 hover:underline"
              >
                How we protect data <ArrowRight className="size-4" aria-hidden />
              </Link>
            </div>
            <p className="mt-6 text-sm text-navy-200">
              Early preview. Everything in the demo is fictitious, and legal research is not yet
              live.
            </p>
          </div>
          <img
            src="/brand/mark-white.svg"
            alt=""
            width={320}
            height={320}
            className="hero-mark hidden md:block md:size-48 lg:size-72 xl:size-80"
          />
        </div>
      </section>

      <ExperienceShowcase />

      <section aria-labelledby="principles" className="border-t border-line-200 bg-white">
        <div className="mx-auto flex max-w-6xl flex-col gap-12 px-4 py-16 md:py-20">
          <div className="flex max-w-3xl flex-col gap-5">
            <h2
              id="principles"
              className="font-display text-4xl leading-tight text-navy-900 md:text-5xl"
            >
              Evidence before fluency.
            </h2>
            <p className="text-lg leading-relaxed text-muted-600">
              Legal work cannot rest on a confident-sounding answer. Buakei AI is designed so that
              every proposition points to the exact source, version and passage behind it, ready for
              you to check.
            </p>
          </div>
          <ul className="grid gap-8 md:grid-cols-3">
            {PRINCIPLES.map((p) => (
              <li key={p.title} className="flex flex-col gap-2 border-t-2 border-navy-900 pt-4">
                <p.icon className="size-5 text-navy-900" aria-hidden />
                <h3 className="font-semibold text-navy-900">{p.title}</h3>
                <p className="text-sm leading-relaxed text-muted-600">{p.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section aria-labelledby="closing" className="on-navy bg-navy-950 text-white">
        <div className="mx-auto flex max-w-6xl flex-col items-start gap-8 px-4 py-12 md:flex-row md:items-center md:justify-between md:py-16">
          <h2
            id="closing"
            className="font-display max-w-2xl text-3xl leading-tight text-balance md:text-4xl"
          >
            See source-grounded legal research in practice.
          </h2>
          <Button asChild variant="inverse" size="lg" className="shrink-0">
            <Link to="/sign-in">Explore the demo</Link>
          </Button>
        </div>
      </section>
    </>
  );
}

export function SecurityPage() {
  useTitle('Security · Buakei AI');
  const today = [
    'This preview contains only fictitious demonstration data.',
    'No client documents can be uploaded, and none should be pasted into the demo.',
    'No AI provider receives any data from this preview.',
    'No payments can be taken.',
  ];
  const planned = [
    'Server-side checks of identity, firm, workspace and matter on every request.',
    'Administrators and support staff have no default access to client matters.',
    'Firm, matter, personal and student data are never mixed in AI context or search.',
    'External AI providers are used only where privacy and retention terms are verified.',
    'Tested backups and a documented incident response before any confidential data is accepted.',
  ];
  const groups = [
    { id: 'today', title: 'Today', items: today },
    { id: 'planned', title: 'Required before real client data', items: planned },
  ];
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-16 px-4 py-16 md:py-20">
      <header className="flex max-w-3xl flex-col gap-6">
        <h1 className="font-display text-4xl leading-tight text-navy-900 md:text-5xl">
          Security and privacy
        </h1>
        <p className="text-lg leading-relaxed text-muted-600">
          We describe only what is true today. Buakei AI has not been independently certified, and
          we will not claim otherwise.
        </p>
      </header>
      {groups.map((group) => (
        <section
          key={group.id}
          aria-labelledby={group.id}
          className="grid gap-6 border-t border-line-200 pt-8 md:grid-cols-[1fr_2fr] md:gap-16"
        >
          <h2 id={group.id} className="font-display text-2xl leading-tight text-navy-900">
            {group.title}
          </h2>
          <ul className="flex flex-col">
            {group.items.map((item) => (
              <li
                key={item}
                className="border-b border-line-200 py-4 leading-relaxed text-ink-900 first:pt-0 last:border-b-0"
              >
                {item}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function NotFoundPage() {
  useTitle('Page not found · Buakei AI');
  return (
    <div className="mx-auto flex max-w-xl flex-col items-center gap-4 px-4 py-24 text-center">
      <Brand showWordmark={false} size="lg" />
      <h1 className="font-display text-4xl text-navy-900">Page not found</h1>
      <p className="text-muted-600">The page you requested does not exist or has moved.</p>
      <Button asChild>
        <Link to="/">Return home</Link>
      </Button>
    </div>
  );
}
